import * as Sentry from '@sentry/react';

const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
const sentryEnvironment = process.env.REACT_APP_ENVIRONMENT || 'development';

// Filter out stack overflows inside scripts injected by the browser
// (e.g., Chrome on iOS): every frame points at the page URL, none at a .js file
export function isInjectedScriptStackOverflow(event: Sentry.ErrorEvent) {
  const exception = event.exception?.values?.[0];
  if (
    exception?.type !== 'RangeError' ||
    !exception.value?.startsWith('Maximum call stack size exceeded')
  ) {
    return false;
  }
  const frames = exception.stacktrace?.frames ?? [];
  return (
    frames.length > 0 &&
    frames.every((frame) => !/\.js(\?|#|$)/.test(frame.filename ?? ''))
  );
}

if (
  sentryDsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.REACT_APP_TECO_LOCAL_MODE !== 'true')
) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // tracesSampleRate: 0.1,
    enableLogs: true,
    // replaysSessionSampleRate: 0.01,
    // replaysOnErrorSampleRate: 1.0,
    beforeSend(event: Sentry.ErrorEvent) {
      // Filter out AxiosError: Network Error caused by aborted requests
      // (e.g., page navigation or component unmount while requests are in-flight)
      const exception = event.exception?.values?.[0];
      if (
        exception?.type === 'AxiosError' &&
        exception?.value === 'Network Error'
      ) {
        const user = event.user;
        Sentry.logger.warn(
          `Suppressed AxiosError: Network Error (likely aborted request) - User: ${user?.id || user?.email || 'unknown'}`,
        );
        return null;
      }

      if (
        exception?.type === 'ReferenceError' &&
        exception?.value === "Can't find variable: EmptyRanges"
      ) {
        const user = event.user;
        Sentry.logger.warn(
          `Suppressed ReferenceError: Can't find variable: EmptyRanges - User: ${user?.id || user?.email || 'unknown'}`,
        );
        return null;
      }

      if (isInjectedScriptStackOverflow(event)) {
        const user = event.user;
        Sentry.logger.warn(
          `Suppressed RangeError from browser-injected script - User: ${user?.id || user?.email || 'unknown'}`,
        );
        return null;
      }

      return event;
    },
  });
}

export function setSentryUser(user?: {
  userId: string;
  nickname?: string;
  clientId?: string;
}) {
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.userId,
    username: user.nickname,
  });
  if (user.clientId) {
    Sentry.setTag('clientId', user.clientId);
  }
}
